import React, { useMemo } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { GrowthBookFeature } from '@gumtree/shared/src/model/experiment';
import { Icon } from '@gumtree/ui-library';
import useWindowWidth from '@gumtree/ui-library/src/utils/use-window-size';
import { LoginModalType } from '@gumtree/shell/src/reducers/common/login-modal';
import { useUserType } from '@gumtree/shared/src/hooks/useUserType';
import { SyiEntrySource, withSyiSource } from '@gumtree/shared/src/util/syi-entry-source';
import { SocialLoginPayload } from './types';
import useFacebookSignIn from './useFacebookSignIn';
import useGoogleSignIn from './useGoogleSignIn';
import useAppleSignIn from './useAppleSignIn';
import {
    StyledFacebookButton,
    StyledGoogleButtonWrapper,
    StyledFacebookLogoSvg,
    StyledFacebookLogoPng,
    StyledSocialFormWrapper,
    StyledErrorBox,
    StyledErrorTitle,
    StyledErrorDescription,
    StyledAppleButton,
    StyledAppleLogo,
} from './style';

const SocialForm = ({ type, modalType, redirectUrl, showApple }: Props) => {
    const userType = useUserType();

    // Get experiment value for login01 - show Apple button if NOT 'A'
    const login01Exper = useFeatureValue(GrowthBookFeature.LOGIN_APPLE, 'B') !== 'A';
    const shouldShowApple = showApple && login01Exper;

    // Detect if device is iOS or Mac using User-Agent
    const isAppleDevice = useMemo(() => {
        if (typeof navigator === 'undefined') {
            return false;
        }

        const userAgent = navigator.userAgent;
        
        // Check for iOS devices (iPhone, iPad, iPod)
        if (/iPhone|iPad|iPod/.test(userAgent)) {
            return true;
        }
        
        // Check for macOS
        if (/Mac OS X|Macintosh/.test(userAgent)) {
            return true;
        }
        
        return false;
    }, []);

    const handleSubmitLogin = async ({ username, password, loginPlatform }: SocialLoginPayload) => {
        const resp = await fetch('/bff-api/login/via-social', {
            method: 'POST',
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                username,
                password,
                loginPlatform,
            }),
            credentials: 'include',
        });

        if (!resp.ok) {
            console.error(`Failed to login: ${resp.status}`);
            return;
        }

        localStorage.setItem('socialLoginJustCompleted', '1');

        const url = window.location.pathname;
        const urlParts = url.split('/');
        const adId = urlParts[urlParts.length - 1];

        if (modalType === LoginModalType.SELL) {
            window.location.assign(withSyiSource('/postad/category', SyiEntrySource.LOGIN_SELL));
        } else if (modalType === LoginModalType.FAVOURITE && redirectUrl) {
            window.location.assign(decodeURIComponent(redirectUrl));
        } else if (redirectUrl) {
            window.location.assign(redirectUrl);
        } else if (modalType === LoginModalType.EMAIL) {
            window.location.assign(`/reply-message/${adId}`);
        } else if (url === '/logout') {
            // reload logout page will cause the logged in account to be logged out
            window.location.assign('/');
        } else if (modalType === LoginModalType.DELIVERY) {
            if (userType === 'STANDARD') {
                window.location.assign(`/create-order?advertId=${adId}`);
            } else {
                window.location.reload();
            }
        } else {
            window.location.reload();
        }
    };

    const { onLaunch, errorMessage: fbErrorMessage } = useFacebookSignIn(handleSubmitLogin, type);
    const {
        onSuccess,
        onError,
        errorMessage: googleErrorMessage,
    } = useGoogleSignIn(handleSubmitLogin, type);
    const {
        onLaunch: onAppleLaunch,
        errorMessage: appleErrorMessage,
        isLoaded: isAppleLoaded,
    } = useAppleSignIn(handleSubmitLogin, type);

    const errorMessage = fbErrorMessage || googleErrorMessage || appleErrorMessage;

    const { width } = useWindowWidth();

    let buttonWidth;

    if (width > 280 && width < 767) {
        buttonWidth = 310;
    } else {
        buttonWidth = 400;
    }

    const appleButton = (
        <StyledAppleButton
            isLoginPage={modalType === null}
            type="button"
            data-testid="apple-sign-in-btn"
            onClick={onAppleLaunch}
            disabled={!isAppleLoaded}
        >
            <StyledAppleLogo />
            Continue with Apple
        </StyledAppleButton>
    );

    const googleButton = (
        <StyledGoogleButtonWrapper isLoginPage={modalType === null}>
            <GoogleLogin
                onSuccess={onSuccess}
                onError={onError}
                text="continue_with"
                logo_alignment="center"
                ux_mode="popup"
                width={modalType ? buttonWidth : undefined}
            />
        </StyledGoogleButtonWrapper>
    );

    const facebookButton = (
        <StyledFacebookButton
            isLoginPage={modalType === null}
            type="button"
            data-testid="facebook-sign-in-btn"
            onClick={onLaunch}
        >
            {modalType ? <StyledFacebookLogoPng /> : <StyledFacebookLogoSvg />}
            Continue with Facebook
        </StyledFacebookButton>
    );

    return (
        <StyledSocialFormWrapper data-testid="social-form-wrapper">
            {errorMessage && (
                <StyledErrorBox>
                    <StyledErrorTitle>
                        <Icon type="circ-error" />
                        Login Error
                    </StyledErrorTitle>
                    <StyledErrorDescription data-testid="social-error-message">
                        {errorMessage}
                    </StyledErrorDescription>
                </StyledErrorBox>
            )}
            {isAppleDevice ? (
                <>
                    {shouldShowApple && appleButton}
                    {googleButton}
                    {facebookButton}
                </>
            ) : (
                <>
                    {googleButton}
                    {shouldShowApple && appleButton}
                    {facebookButton}
                </>
            )}
        </StyledSocialFormWrapper>
    );
};

export default SocialForm;

interface Props {
    modalType: LoginModalType | null;
    redirectUrl?: string | null;
    type: 'login' | 'register';
    showApple?: boolean;
}
